import { Icon } from "@afterpaytouch/core";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useState } from "react";

import { Locale } from "@/types/locale";

import { ICONS } from "../../icons";
import Header from "../../text/header";
import ChatDialog, { getChatDeepLink } from "../chat-dialog/chat-dialog";
import styles from "../landing/landing.module.scss";
import { ARTICLES_WITHOUT_EXPLORE_MORE, getExploreMoreHref } from "../utils";
import ArticleTile from "./article-tile";

const SectionWrapper = ({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) => {
    return <div className={`${styles.sectionWrapper} ${className}`}>{children}</div>;
};

const GridWrapper = ({ children, column = 2, className = "" }) => {
    return (
        <div className={`${styles[`col${column}`]} ${styles.gridWrapper} ${className}`}>
            {children}
        </div>
    );
};

const pushIcon = (
    <div className={styles.iconWrapper}>
        <ICONS.Push color="currentColor" />
    </div>
);

const chatIcon = (
    <div className={styles.iconWrapper}>
        <Icon.Chatting size="Small" />
    </div>
);

const LOCALES_WITH_CALL = ["en-US", "en-CA", "fr-CA", "en-GB", "en-AU", "en-NZ"];
const LOCALES_WITH_EXPLORE_MORE = ["en-AU", "en-NZ", "en-GB"];
// These locales offer a phone number instead of the webform ("Submit a form").
const LOCALES_WITHOUT_FORM = ["en-US", "en-CA", "fr-CA", "en-GB", "en-AU", "en-NZ"];

const MERCHANT_MAILTO: Record<string, string> = {
    "en-GB": "mailto:uk-clearpay-merchant-admin@squareup.com",
    "es-ES": "mailto:uk-clearpay-merchant-admin@squareup.com",
    "fr-FR": "mailto:uk-clearpay-merchant-admin@squareup.com",
    "it-IT": "mailto:uk-clearpay-merchant-admin@squareup.com",
    "en-AU": "mailto:anz-afterpay-merchant-admin@squareup.com",
    "en-NZ": "mailto:anz-afterpay-merchant-admin@squareup.com",
    "en-US": "mailto:na-afterpay-merchant-admin@squareup.com",
    "en-CA": "mailto:na-afterpay-merchant-admin@squareup.com",
    "fr-CA": "mailto:na-afterpay-merchant-admin@squareup.com",
};

type HelpContactFooterProps = {
    type?: "consumer" | "merchant";
    articleToken?: string;
};

export const HelpContactFooter = ({ type = "consumer", articleToken }: HelpContactFooterProps) => {
    const { t } = useTranslation();
    const { locale } = useRouter();
    const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);

    if (type === "merchant") {
        const mailtoHref = MERCHANT_MAILTO[locale ?? ""] ?? MERCHANT_MAILTO["en-US"];
        return (
            <SectionWrapper>
                <Header
                    renderAs="h6"
                    title={t("help.contact.title", "Contact us")}
                    className={styles.helpSubTitle}
                />
                <GridWrapper column={1}>
                    <ArticleTile
                        title={t("help.contact.email.title", "Send a message")}
                        href={mailtoHref}
                        subtitle={t(
                            "help.contact.email.subTitle",
                            "Get in touch with our team via email"
                        )}
                        leftIcon={
                            <div className={styles.iconWrapper}>
                                <Icon.Email size="Small" />
                            </div>
                        }
                        rightIcon={pushIcon}
                    />
                </GridWrapper>
            </SectionWrapper>
        );
    }

    const hasCall = LOCALES_WITH_CALL.includes(locale ?? "");
    const isExploreMoreExcludedArticle =
        !!articleToken && ARTICLES_WITHOUT_EXPLORE_MORE.has(articleToken);
    const hasExploreMore =
        LOCALES_WITH_EXPLORE_MORE.includes(locale ?? "") && !isExploreMoreExcludedArticle;
    const hasForm = !LOCALES_WITHOUT_FORM.includes(locale ?? "");

    const chatTitle = t("help.contact.chat.title", "Start a chat");
    const chatSubtitle = t("help.contact.chat.subTitle", "Open your mobile app to chat 24/7");

    return (
        <>
            <SectionWrapper>
                <Header
                    renderAs="h6"
                    title={t("help.contact.title", "Contact us")}
                    className={styles.helpSubTitle}
                />
                <GridWrapper column={1}>
                    {/* Mobile: hand off directly to the native app's support flow */}
                    <ArticleTile
                        className={styles.hideDesktop}
                        title={chatTitle}
                        href={getChatDeepLink(locale ?? "en-US")}
                        subtitle={chatSubtitle}
                        leftIcon={chatIcon}
                        rightIcon={pushIcon}
                    />
                    {/* Desktop: show a scannable QR code in a modal */}
                    <ArticleTile
                        className={styles.hideMobile}
                        title={chatTitle}
                        onClick={() => setIsChatDialogOpen(true)}
                        subtitle={chatSubtitle}
                        leftIcon={chatIcon}
                        rightIcon={pushIcon}
                    />
                </GridWrapper>
                <GridWrapper column={hasCall && hasForm ? 2 : 1}>
                    {hasCall && (
                        <ArticleTile
                            title={t("help.contact.call.title", "Call us")}
                            href={`tel:${t("help.contact.call.number")}`}
                            subtitle={t(
                                "help.contact.call.subTitle",
                                "Available daily, 8 AM-9:30 PM ET"
                            )}
                            leftIcon={
                                <div className={styles.iconWrapper}>
                                    <Icon.Phone size="Small" />
                                </div>
                            }
                            rightIcon={pushIcon}
                        />
                    )}
                    {hasForm && (
                        <ArticleTile
                            title={t("help.contact.form.title", "Submit a form")}
                            href="/submit-form"
                            subtitle={t(
                                "help.contact.form.subTitle",
                                "Get help via email follow-up"
                            )}
                            leftIcon={
                                <div className={styles.iconWrapper}>
                                    <Icon.Email size="Small" />
                                </div>
                            }
                            rightIcon={pushIcon}
                        />
                    )}
                </GridWrapper>
                {hasExploreMore && (
                    <GridWrapper column={1}>
                        <ArticleTile
                            title={t("help.contact.explore.title", "Explore more options")}
                            href={getExploreMoreHref(locale as Locale)}
                            subtitle={t(
                                "help.contact.explore.subTitle",
                                "Browse more ways to get help"
                            )}
                            leftIcon={
                                <div className={styles.iconWrapper}>
                                    <Icon.Search size="Small" />
                                </div>
                            }
                            rightIcon={pushIcon}
                        />
                    </GridWrapper>
                )}
            </SectionWrapper>
            {isChatDialogOpen && <ChatDialog onClose={() => setIsChatDialogOpen(false)} />}
        </>
    );
};
